import { countUploadsInFlight } from "@frontend-shared/utils";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { useMeetingUploads } from "@/hooks/use-meeting-uploads";

// NOTE: Counts the meeting uploads still in flight and shows them next to the
// sidebar's Uploads entry. Since NSAI-5141 an upload no longer pins the user to
// the Uploads page, so this is what keeps a background transfer visible from
// anywhere — plus a one-click way back to the full per-file progress list.
// Renders nothing while no upload is running.
const UploadsInProgressBadgeComponent = (): JSX.Element | null => {
  const { t } = useTranslation("sidebar");
  const { uploads } = useMeetingUploads();

  // NOTE: NSAI-5241 — shared with the mobile sidebar badge, so the two cannot
  // drift apart on which statuses count as "in progress".
  const inFlightCount = countUploadsInFlight({ uploads });

  if (inFlightCount === 0) {
    return null;
  }

  return (
    <span
      className="ms-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary tabular-nums"
      aria-label={t("menu.uploads_in_progress", { count: inFlightCount })}
      title={t("menu.uploads_in_progress", { count: inFlightCount })}
      data-testid="sidebar-uploads-in-progress-badge"
    >
      {inFlightCount}
    </span>
  );
};

export const UploadsInProgressBadge = memo(UploadsInProgressBadgeComponent);
